import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Typography,
  TextField,
  Chip,
  LinearProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  BsFillPeopleFill,
  BsSearch,
  BsPencilSquare,
  BsShieldCheck,
  BsExclamationTriangleFill,
  BsCheckCircleFill,
  BsInfinity,
  BsArrowRepeat,
} from "react-icons/bs";
import Swal from "sweetalert2";
import { COMPANY, COMPANYALL } from "../../services/actions";
import Breadcrumb from "../../extra2/Breadcrumb";
import RDialog from "../extra/RDialog";

export default function CompanyUserLimits() {
  const dispatch = useDispatch();
  const companyallState = useSelector((state) => state?.companyallState);
  const companyState = useSelector((state) => state?.companyState);
  const user = useSelector((state) => state?.loginState?.auth);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'unlimited' | 'limited' | 'reached'
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [maxUsersInput, setMaxUsersInput] = useState(0);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = Boolean(user?.is_superuser || user?.main_role === "SuperAdmin");

  const loadCompanies = () => {
    dispatch({ type: `${COMPANYALL}_GET`, payload: "company/0/" });
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (companyState?.success) {
      loadCompanies();
    }
  }, [companyState?.data]);

  const rawCompanies = useMemo(() => {
    const list = companyallState?.data?.results || [];
    return Array.isArray(list) ? list : [];
  }, [companyallState]);

  // Summary statistics
  const stats = useMemo(() => {
    let total = rawCompanies.length;
    let totalUsers = 0;
    let unlimited = 0;
    let limited = 0;
    let reached = 0;

    rawCompanies.forEach((c) => {
      const uCount = Number(c.user_count || 0);
      const mUsers = Number(c.max_users || 0);
      totalUsers += uCount;
      if (mUsers === 0) {
        unlimited++;
      } else {
        limited++;
        if (uCount >= mUsers) {
          reached++;
        }
      }
    });

    return { total, totalUsers, unlimited, limited, reached };
  }, [rawCompanies]);

  const filteredCompanies = useMemo(() => {
    return rawCompanies.filter((c) => {
      const nameMatch =
        (c.company_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.company_email || "").toLowerCase().includes(searchTerm.toLowerCase());
      if (!nameMatch) return false;

      const mUsers = Number(c.max_users || 0);
      const uCount = Number(c.user_count || 0);

      if (filterMode === "unlimited") return mUsers === 0;
      if (filterMode === "limited") return mUsers > 0;
      if (filterMode === "reached") return mUsers > 0 && uCount >= mUsers;
      return true;
    });
  }, [rawCompanies, searchTerm, filterMode]);

  const handleOpenEdit = (comp) => {
    setSelectedCompany(comp);
    setMaxUsersInput(Number(comp.max_users || 0));
    setEditModalOpen(true);
  };

  const handleSaveLimit = () => {
    if (!selectedCompany) return;
    const numLimit = parseInt(maxUsersInput, 10);
    if (isNaN(numLimit) || numLimit < 0) {
      Swal.fire({
        icon: "error",
        title: "Invalid Input",
        text: "Please enter a non-negative number (0 for unlimited).",
      });
      return;
    }

    setSaving(true);
    dispatch({
      type: `${COMPANY}_PATCH`,
      payload: {
        id: selectedCompany.id,
        max_users: numLimit,
      },
      callback: (res) => {
        setSaving(false);
        setEditModalOpen(false);
        Swal.fire({
          icon: "success",
          title: "Limit Updated",
          text: `Maximum user limit for "${selectedCompany.company_name}" has been set to ${
            numLimit === 0 ? "Unlimited (0)" : numLimit
          }.`,
          timer: 2000,
          showConfirmButton: false,
        });
        loadCompanies();
      },
    });
  };

  if (!isSuperAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: "center" }}>
        <Card className="card-bg shadow" sx={{ p: 4, borderRadius: "16px" }}>
          <BsExclamationTriangleFill size={48} color="#ef4444" />
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>
            Access Restricted
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: "#64748b" }}>
            Only Super Administrators can configure company user limits.
          </Typography>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className="py-2" sx={{ px: { xs: 2, md: 3 } }}>
      {/* Widelapse Standard Top Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Breadcrumb title="Permissions" subtitle="Company User Limits" />
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/* Quick summary badges */}
          <Chip
            size="small"
            label={`Total: ${stats.total}`}
            sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.05)" }}
          />
          <Chip
            size="small"
            label={`Users: ${stats.totalUsers}`}
            sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(56, 189, 248, 0.15)", color: "#0284c7" }}
          />
          <Chip
            size="small"
            label={`Unlimited: ${stats.unlimited}`}
            sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(34, 197, 94, 0.15)", color: "#16a34a" }}
          />
          {stats.reached > 0 && (
            <Chip
              size="small"
              label={`At Limit: ${stats.reached}`}
              sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(239, 68, 68, 0.15)", color: "#dc2626" }}
            />
          )}

          <Button
            variant="contained"
            onClick={loadCompanies}
            startIcon={<BsArrowRepeat />}
            sx={{
              backgroundColor: "#FFCC33",
              color: "#000",
              "&:hover": { backgroundColor: "#f5bc18" },
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 2,
              height: "36px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Main Container styled like Widelapse's standard cards */}
      <div className="card-bg p-4 md:p-5 rounded-xl shadow-[0px_4px_20px_0px_rgba(0,0,0,0.06)] border border-gray-100 flex flex-col">
        {/* Search & Tabs Filter Bar */}
        <Box
          sx={{
            mb: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Tabs Filter */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[
              { key: "all", label: `All Companies (${rawCompanies.length})` },
              { key: "unlimited", label: `Unlimited Quota (${stats.unlimited})` },
              { key: "limited", label: `Limited Quota (${stats.limited})` },
              { key: "reached", label: `Quota Reached (${stats.reached})` },
            ].map((tab) => {
              const isActive = filterMode === tab.key;
              return (
                <Button
                  key={tab.key}
                  size="small"
                  variant={isActive ? "contained" : "outlined"}
                  onClick={() => setFilterMode(tab.key)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    px: 1.8,
                    height: "36px",
                    backgroundColor: isActive ? "#FFCC33" : "transparent",
                    color: isActive ? "#000" : "inherit",
                    borderColor: isActive ? "#FFCC33" : "rgba(0,0,0,0.12)",
                    "&:hover": {
                      backgroundColor: isActive ? "#f5bc18" : "rgba(255, 204, 51, 0.15)",
                      borderColor: "#FFCC33",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Box>

          {/* Search Input */}
          <TextField
            size="small"
            placeholder="Search by company or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "rgba(0,0,0,0.02)",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BsSearch color="#94a3b8" size={13} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Table Container */}
        <TableContainer
          component={Paper}
          className="card-bg"
          sx={{
            borderRadius: "14px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contact / Key Person</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Current Users
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Max User Limit
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status & Usage</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      No companies found matching criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((comp) => {
                  const uCount = Number(comp.user_count || 0);
                  const mUsers = Number(comp.max_users || 0);
                  const isUnlimited = mUsers === 0;
                  const isReached = !isUnlimited && uCount >= mUsers;
                  const percent = isUnlimited ? 0 : Math.min(100, Math.round((uCount / mUsers) * 100));

                  return (
                    <TableRow
                      key={comp.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        backgroundColor: isReached ? "rgba(239, 68, 68, 0.05)" : "inherit",
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              background: isReached ? "#fee2e2" : "rgba(255, 204, 51, 0.2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isReached ? "#dc2626" : "#b45309",
                              fontWeight: 700,
                              fontSize: "14px",
                            }}
                          >
                            {(comp.company_name || "C").charAt(0).toUpperCase()}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {comp.company_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {comp.company_email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {comp.keyperson || "—"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {comp.company_phone || comp.keypersonphone || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={`${uCount} users`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            borderRadius: "8px",
                            backgroundColor: "rgba(0,0,0,0.06)",
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        {isUnlimited ? (
                          <Chip
                            icon={<BsInfinity style={{ fontSize: 13 }} />}
                            label="Unlimited (0)"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(34, 197, 94, 0.15)",
                              color: "#16a34a",
                              fontWeight: 700,
                              borderRadius: "8px",
                            }}
                          />
                        ) : (
                          <Chip
                            label={`Max: ${mUsers}`}
                            size="small"
                            sx={{
                              backgroundColor: isReached ? "rgba(239, 68, 68, 0.15)" : "rgba(59, 130, 246, 0.15)",
                              color: isReached ? "#dc2626" : "#2563eb",
                              fontWeight: 700,
                              borderRadius: "8px",
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 200 }}>
                        <Box sx={{ width: "100%" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: isUnlimited ? "#16a34a" : isReached ? "#ef4444" : "#3b82f6",
                              }}
                            >
                              {isUnlimited
                                ? "N Users Allowed (No Limit)"
                                : isReached
                                ? `Limit Reached (${uCount}/${mUsers})`
                                : `${mUsers - uCount} slots remaining (${uCount}/${mUsers})`}
                            </Typography>
                            {!isUnlimited && (
                              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                                {percent}%
                              </Typography>
                            )}
                          </Box>
                          {!isUnlimited && (
                            <LinearProgress
                              variant="determinate"
                              value={percent}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: "rgba(0,0,0,0.08)",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor: isReached ? "#ef4444" : percent > 75 ? "#f59e0b" : "#FFCC33",
                                  borderRadius: 3,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<BsPencilSquare size={13} />}
                          onClick={() => handleOpenEdit(comp)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "10px",
                            px: 1.5,
                            borderColor: "rgba(0,0,0,0.15)",
                            color: "inherit",
                            "&:hover": {
                              borderColor: "#FFCC33",
                              backgroundColor: "rgba(255, 204, 51, 0.15)",
                              color: "#000",
                            },
                          }}
                        >
                          Set Limit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Edit Limit Modal using Widelapse standard RDialog */}
      <RDialog
        title="Configure Company User Limit"
        open={editModalOpen}
        handleClose={() => setEditModalOpen(false)}
      >
        <Box sx={{ minWidth: { xs: 280, sm: 380 }, py: 1 }}>
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              backgroundColor: "rgba(0,0,0,0.03)",
              borderRadius: "12px",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {selectedCompany?.company_name}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {selectedCompany?.company_email}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={`Currently Created: ${selectedCompany?.user_count || 0} users`}
                size="small"
                sx={{
                  fontWeight: 700,
                  backgroundColor: "rgba(255, 204, 51, 0.2)",
                  color: "#000",
                  borderRadius: "8px",
                }}
              />
            </Box>
          </Box>

          <TextField
            autoFocus
            label="Maximum Number of Users"
            type="number"
            fullWidth
            inputProps={{ min: 0 }}
            value={maxUsersInput}
            onChange={(e) => setMaxUsersInput(e.target.value)}
            helperText="Enter 0 for unlimited (N) user creation. Any number > 0 restricts creation to that maximum."
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            }}
          />

          <Box
            sx={{
              mt: 2.5,
              p: 1.5,
              borderRadius: "12px",
              backgroundColor: "rgba(255, 204, 51, 0.1)",
              border: "1px solid rgba(255, 204, 51, 0.3)",
            }}
          >
            <Typography variant="caption" sx={{ display: "block", color: "inherit" }}>
              💡 <strong>How limits work:</strong> If set to <strong>0</strong>, the company can create any number of users. If set to a specific number (e.g. 5), attempting to create more users will block further user creation.
            </Typography>
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              onClick={() => setEditModalOpen(false)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#64748b",
                borderRadius: "10px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSaveLimit}
              sx={{
                backgroundColor: "#FFCC33",
                color: "#000",
                "&:hover": { backgroundColor: "#f5bc18" },
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "12px",
                px: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              {saving ? "Saving..." : "Save Limit"}
            </Button>
          </Box>
        </Box>
      </RDialog>
    </Container>
  );
}
